import { CustomerWithContacts, InsertCustomer, SelectContact } from "@/database/schema";
import { useClickOutside } from "@/utils";
import { Plus, SquarePen, Trash2, Undo2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Contact, Country, EditCustomerForm } from "@/types";
import { countries } from "@/shared/countriesDropdown/countries";
import { CountriesDropdown } from "@/shared/countriesDropdown/CountiesDropdown";
import { Flip, toast, ToastContainer } from "react-toastify";
import { contactsRepository } from "@/database/repositories/contactsRepository";
import { CSSTransition } from "react-transition-group";
import "./CustomerForm.scss";
import { useDb } from "@/database/provider/useDb";
import { $customers } from "@/stores";

type Props = {
    customer?: CustomerWithContacts,
    title: string,
    action: (data: InsertCustomer, contacts: Contact[]) => void,
    close: () => void
}

export const CustomerForm = ({ customer, title, action, close }: Props) => {
    const db = useDb();
    const [customerData, setCustomerData] = useState(customer);
    const [customerCountries, setCustomerCountries] = useState<Country[]>(countries);
    const [selectCountryOpen, setSelectCountryOpen] = useState(false);
    const { register, setValue, handleSubmit, reset, setFocus, clearErrors, watch, trigger, formState: { errors } }
        = useForm<EditCustomerForm>({
            defaultValues: {
                name: "",
                address: null,
                contacts: [{
                    email: "",
                    firstName: "",
                    lastName: "",
                    phone: "",
                }]
            }
        });
    const formAreaRef = useRef(null);
    const [editContactId, setEditContactId] = useState<number | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const [lockSaveButton, setLockSaveButton] = useState(false);
    const [displayArchivedContacts, setDisplayArchivedContacts] = useState(false);
    const formData = watch();
    const handleCountrySearch = (event: React.ChangeEvent<HTMLInputElement>,
        set: React.Dispatch<React.SetStateAction<Country[]>>) => {
        if (!selectCountryOpen) setSelectCountryOpen(true);
        const { value } = event.target;
        const filtered = countries.filter((country) => country.name.toLowerCase().includes(value.toLowerCase()));
        if (filtered.length === 1
            && filtered[0].name.toLowerCase() === value.toLowerCase()) {
            setValue("country", filtered[0].name);
            setSelectCountryOpen(false);
        }
        set(filtered);
    }

    const handleDeleteContact = async (i: number) => {
        try {
            let contacts: Contact[] | SelectContact[] = []
            if (customerData) {
                const contact = customerData.activeContacts[i];
                await contactsRepository.deleteContact(db, contact.id);
                contacts = customerData.activeContacts.filter((item) => item.id !== contact.id);
                customerData.archivedContacts.push(contact);
                formData.contacts = contacts;
                customerData.activeContacts = contacts as SelectContact[];
                setConfirmDeleteId(null);

                toast.success(`Contact archived!`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: false,
                    pauseOnHover: true,
                    theme: "light",
                    transition: Flip,
                });
            }
            else contacts = formData.contacts.filter((_, index) => index !== i);
            if (contacts.length > 0) setValue("contacts", contacts, { shouldValidate: true });
            else {
                setValue("contacts", [{
                    email: "",
                    firstName: "",
                    lastName: "",
                    phone: "",
                }], { shouldValidate: true });
                setEditContactId(0);
            }
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }

    const handleSaveContact = async (editContactId: number) => {
        try {
            const valid = await trigger([`contacts.${editContactId}.email`]);
            if (valid) {
                if (customerData) {
                    const contact = customerData.activeContacts[editContactId];
                    if (contact) {
                        await contactsRepository.updateContact(db, contact);
                    }
                    else {
                        const contact = { ...formData.contacts[editContactId], customerId: customerData.id };
                        const res = await contactsRepository.addContact(db, contact);
                        const customer = $customers.value.values.find((item) => item.id === customerData.id);
                        customer?.activeContacts.push(res);
                        if (customerData) customerData.activeContacts.push(res);
                    }
                    setCustomerData(customerData);
                    setEditContactId(null);
                    setLockSaveButton(false);
                    toast.success(`Contact saved!`, {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: true,
                        closeOnClick: false,
                        pauseOnHover: true,
                        theme: "light",
                        transition: Flip,
                    });
                }
                else {
                    setEditContactId(null);
                    setLockSaveButton(false);
                }
            }
            else setFocus(`contacts.${editContactId}.email`);
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }
    const handleRestoreContact = async (id: number) => {
        try {
            const contact = await contactsRepository.restoreContact(db, id);
            // @ts-ignore
            customerData.archivedContacts = customerData.archivedContacts.filter((item) => item.id !== id);
            // @ts-ignore
            customerData.activeContacts.unshift(contact);
            const formContacts = formData.contacts;
            formContacts.unshift(contact);
            setValue("contacts", formContacts, { shouldValidate: true })
            toast.success(`Contact restored!`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }

    useClickOutside(formAreaRef, () => {
        close();
    });

    useEffect(() => {
        if (customer) {
            reset({
                ...customer,
                country: customer.country.name,
                contacts: customer.activeContacts
            });
            if (customer.activeContacts.length === 0) {
                setEditContactId(0);
                setLockSaveButton(true);
            }
        }
        else {
            setEditContactId(0);
            setLockSaveButton(true);
        }
    }, [customer, reset]);

    return (<div className="edit-customer" ref={formAreaRef}>
        <ToastContainer />
        <form onSubmit={handleSubmit(async (data) => {
            const { contacts, ...rest } = data;
            await action({
                ...rest,
                country: countries.find((item) => item.name === data.country) as Country,
            }, contacts);
            close();
        })}>
            <div className="edit-customer__head">
                <span className="edit-customer__head-text">
                    <b>{title}</b>
                </span>
                <button type="button" onClick={() => {
                    close();
                }}>
                    <X width={18} height={18} />
                </button>
            </div>
            <div className="edit-customer__inputs">
                <div className="edit-customer__inputs-row">
                    <div className="edit-customer__input-container">
                        <span>Customer name:</span>
                        <input
                            className={`${errors.name ? "input-error" : ""}`}
                            {...register("name", { required: true })}
                            placeholder="Enter customer name"
                            autoComplete="off" />
                    </div>
                    <div className="edit-customer__input-container">
                        <span>Country:</span>
                        <input
                            className={`${errors.country ? "input-error" : ""}`}
                            {...register("country", { required: true })}
                            placeholder="Enter customer country"
                            onChange={(e) => {
                                handleCountrySearch(e, setCustomerCountries);
                            }}
                            onFocus={() => {
                                setCustomerCountries(countries);
                                setSelectCountryOpen(true);
                            }}
                            autoComplete="off" />
                        <CSSTransition in={selectCountryOpen}
                            timeout={200}
                            classNames="fade"
                            exit={false}
                            unmountOnExit>
                            <div className="edit-customer__countries-container">
                                <CountriesDropdown countries={customerCountries}
                                    onSelect={(country: Country) => {
                                        setValue("country", country.name, { shouldValidate: true });
                                        setSelectCountryOpen(false);
                                    }}
                                    close={() => {
                                        setSelectCountryOpen(false);
                                    }} />
                            </div>
                        </CSSTransition>
                    </div>
                </div>
                <div className="edit-customer__input-container">
                    <span>Address:</span>
                    <textarea {...register("address")}
                        placeholder="Enter customer address" />
                </div>
                <div className="edit-customer__contacts">
                    <div className="edit-customer__contacts-header">
                        <div className="edit-customer__contacts-label">Contacts:</div>
                        {(customer) && <div className="edit-customer__contacts-switch">
                            <button
                                className={`edit-customer__switch-customers ${!displayArchivedContacts ? "edit-customer__active" : ""}`}
                                type="button"
                                onClick={() => {
                                    setDisplayArchivedContacts(false)
                                }}>
                                Active
                            </button>
                            <button
                                className={`edit-customer__switch-customers ${displayArchivedContacts ? "edit-customer__active" : ""}`}
                                type="button"
                                onClick={() => {
                                    setDisplayArchivedContacts(true)
                                }}>
                                Archived
                            </button>
                        </div>}
                    </div>
                    {(editContactId === null && !displayArchivedContacts) && <>
                        <div className="edit-customer__contacts-container">
                            {formData.contacts.length > 0 && <div>
                                {editContactId === null && <div>
                                    <ul className="edit-customer__contacts-list">
                                        {formData.contacts.map((contact, i) => <li className="edit-customer__contact" key={i}>
                                            {confirmDeleteId === i ? <div className="edit-customer__confirm-delete">
                                                <div className="edit-customer__confirm-delete-text">
                                                    <p>Archive </p>
                                                    <b>{contact.email}?</b>
                                                </div>
                                                <div className="edit-customer__confirm-delete-buttons">
                                                    <button className="edit-customer__delete-contact" type="button" onClick={() => {
                                                        handleDeleteContact(i);
                                                    }}>Archive</button>
                                                    <button className="edit-customer__cancel-delete" type="button" onClick={() => {
                                                        setConfirmDeleteId(null);
                                                    }}>Cancel</button>
                                                </div>
                                            </div> : <>
                                                <p>{contact.firstName} {contact.lastName} {contact.email} {contact.phone}</p>
                                                <div className="edit-customer__contact-controls">
                                                    <button type="button" onClick={() => {
                                                        setEditContactId(i);
                                                        clearErrors(`contacts`);
                                                        setValue(`contacts.${i}`, contact);
                                                    }}>
                                                        <SquarePen width={20} height={20} />
                                                    </button>
                                                    <button type="button" onClick={() => {
                                                        setConfirmDeleteId(i);
                                                    }}>
                                                        <Trash2 width={20} height={20} style={{ color: "#e63e49" }} />
                                                    </button>
                                                </div>
                                            </>}
                                        </li>)}
                                    </ul>
                                    <button className="edit-customer__add-contact" type="button" onClick={() => {
                                        formData.contacts.push({
                                            firstName: "",
                                            lastName: "",
                                            email: "",
                                            phone: "",
                                        })
                                        reset(formData);
                                        setEditContactId(formData.contacts.length - 1);
                                        setLockSaveButton(true);
                                    }}>
                                        <Plus width={12} height={12} />
                                        Add contact
                                    </button>
                                </div>}
                            </div>}
                        </div>
                    </>}
                    {(displayArchivedContacts && customerData) && <div>
                        {customerData.archivedContacts.length > 0 ? <>
                            <ul className="edit-customer__contacts-list">
                                {customerData.archivedContacts.map((contact, i) => <li className="edit-customer__contact" key={i}>
                                    <p>{contact.firstName} {contact.lastName} {contact.email} {contact.phone}</p>
                                    <div className="edit-customer__contact-controls">
                                        <button type="button" onClick={() => {
                                            handleRestoreContact(contact.id);
                                            setEditContactId(null);
                                            setLockSaveButton(false);
                                            if (customerData.activeContacts.length === 0) formData.contacts.pop()
                                        }}>
                                            <Undo2 width={20} height={20} />
                                        </button>
                                    </div>
                                </li>)}
                            </ul>
                        </> : <div className="edit-customer__empty-contacts">
                            No archived contacts.
                        </div>}
                    </div>}
                    {((editContactId !== null && !displayArchivedContacts)) && <div className="edit-customer__edit-customer">
                        <div className="edit-customer__inputs-row">
                            <div className="edit-customer__input-container">
                                <input
                                    {...register(`contacts.${editContactId}.firstName`)}
                                    placeholder="First name"
                                    autoComplete="off" />
                            </div>
                            <div className="edit-customer__input-container">
                                <input
                                    {...register(`contacts.${editContactId}.lastName`)}
                                    placeholder="Last name"
                                    autoComplete="off" />
                            </div>
                        </div>
                        <input
                            className={`${errors.contacts ? "input-error" : ""}`}
                            {...register(`contacts.${editContactId}.email`, {
                                required: true,
                                pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                    message: "Invalid email format",
                                },
                            })}
                            onChange={() => {
                                clearErrors(`contacts.${editContactId}.email`)
                            }}
                            placeholder="Email"
                            type="email"
                            autoComplete="on"
                        />
                        <input
                            {...register(`contacts.${editContactId}.phone`)}
                            placeholder="Phone number"
                            type="phone"
                            autoComplete="off" />

                        <div className="edit-customer__customer-buttons">
                            <button className="edit-customer__save-contact" type="button" disabled={errors.contacts ? true : false} onClick={() => {
                                handleSaveContact(editContactId);
                            }}>Save contact</button>
                            {formData.contacts.length - 1 > 0 &&
                                <button className="edit-customer__cancel-edit" type="button" onClick={async () => {
                                    if (customerData) {
                                        setValue("contacts", customerData.activeContacts);
                                    }
                                    else {
                                        formData.contacts.pop()
                                        setValue("contacts", formData.contacts);
                                    }
                                    setLockSaveButton(false);
                                    clearErrors(`contacts.${editContactId}.email`);
                                    setEditContactId(null);
                                }}>Cancel</button>}
                        </div>
                    </div>}
                </div>

            </div>
            <div className="edit-customer__button-container">
                <button className="edit-customer__save" type="submit" disabled={(Object.keys(errors).length > 0 || lockSaveButton)}>
                    Save
                </button>
            </div>
        </form >
    </div >)
}


